import React, { Component, Fragment } from 'react'
import DashboardLayout from '../../components/Admin/DashboardLayout'
import { MDBBtn, MDBCol, MDBContainer, MDBRow } from 'mdb-react-ui-kit'
import MDB4Datatable from './../../components/content/MDB4Datatable/MDB4Datatable.component'
import { toast } from 'react-toastify'
import { DivisionFactory, UserFactory, UserAccomplishmentFactory } from './../../shared/factories'
import UserAccomplishmentService from './../../shared/services/UserAccomplishment.service'
import { getHeadshot } from './../../shared/utilities/getHeadshot.function'
import { RecognizeAgentBtn } from './components/RecognizeAgentBtn'
import { addWeeks, endOfWeek, format, getWeek, startOfWeek, subWeeks } from 'date-fns'
import moment from 'moment-timezone'
import './WeeklyAdminReports.scss'

const EMPTY_DATATABLE = {
  columns: [
    { label: 'Rank', field: 'rank', sort: 'disabled', width: 50 },
    { label: 'Agent', field: 'agent', sort: 'disabled', width: 200 },
    { label: 'Leader', field: 'leader', sort: 'disabled', width: 150 },
    { label: 'Division', field: 'division', sort: 'disabled', width: 150 },
    { label: 'Date Accomplished', field: 'date', sort: 'asc', width: 150 },
  ],
  rows: [],
}

// `accomplished_at` is stored in server UTC; the business week is Mon-Sun U.S. Eastern.
const REPORT_TZ = 'America/New_York'

const etWeekRangeUtc = date => {
  const start = moment.tz(format(date, 'yyyy-MM-dd'), 'YYYY-MM-DD', REPORT_TZ).startOf('isoWeek')

  return {
    start: start.clone().utc().format('YYYY-MM-DD HH:mm:ss'),
    stop: start.clone().endOf('isoWeek').utc().format('YYYY-MM-DD HH:mm:ss'),
  }
}

const fetchUserAccomplishments = async (start, stop) => {
  // Only first-time Signature Agents belong on this report; returning agents are filtered server-side.
  const response = {},
    ids = (await UserAccomplishmentService.newSigAgentIds({ search: { start, stop } })) || [],
    accomplishments = ids.length
      ? await UserAccomplishmentFactory.search({
          search: { id: ids },
          pagination: false,
        })
      : []

  ;(accomplishments || []).forEach(a => (response[a.get('user_id')] = { accomplishment: a, user: null }))

  const userIds = Object.keys(response),
    users = userIds.length
      ? (await UserFactory.search({
          search: { id: userIds },
          pagination: false,
        })) || []
      : []
  users.forEach(u => (response[u.get('id')].user = u))

  const divisionIds = [...new Set(users.map(u => parseInt(u.get('u_devision'))).filter(id => id > 0))],
    divisions = divisionIds.length
      ? (await DivisionFactory.search({
          search: { id: divisionIds },
          pagination: false,
        })) || []
      : [],
    // Leader is the agent's direct upline, whatever their rank -- not the division owner or recruiter.
    uplineIds = [...new Set(users.map(u => parseInt(u.get('u_upline_id'))).filter(id => id > 0))],
    uplines = uplineIds.length
      ? (await UserFactory.search({
          search: { id: uplineIds },
          pagination: false,
        })) || []
      : []

  const divisionsById = Object.fromEntries(divisions.map(d => [parseInt(d.get('id')), d])),
    uplinesById = Object.fromEntries(uplines.map(o => [parseInt(o.get('id')), o]))

  Object.values(response).forEach(row => {
    const division = row.user && divisionsById[parseInt(row.user.get('u_devision'))],
      upline = row.user && uplinesById[parseInt(row.user.get('u_upline_id'))]

    row.division = division ? division.get('d_name') : ''
    row.leader = upline ? `${upline.get('u_fname')} ${upline.get('u_lname')}` : ''
  })

  return response
}

class CompletedSigAgents extends Component {
  constructor(props) {
    super(props)

    this.state = {
      loading: false,
      currentDate: new Date(),
      dateFrom: startOfWeek(new Date(), { weekStartsOn: 1 }),
      dateTo: endOfWeek(new Date(), { weekStartsOn: 1 }),
      tableData: null,
      accomplishments: [],
    }
  }

  componentDidMount() {
    this._fetchReport()
  }

  _fetchReport = async () => {
    this.setState({ loading: true })

    try {
      const { start, stop } = etWeekRangeUtc(subWeeks(this.state.dateFrom, 1)),
        // Grouped by division, then date, so each division's agents can be copied together.
        accomplishments = Object.values(await fetchUserAccomplishments(start, stop))
          .filter(row => row.user)
          .sort(
            (a, b) =>
              `${a.division || ''}`.localeCompare(`${b.division || ''}`) ||
              `${a.accomplishment.get('accomplished_at') || ''}`.localeCompare(
                `${b.accomplishment.get('accomplished_at') || ''}`
              )
          )

      this.setState({ tableData: this._buildTableData(accomplishments) })
    } catch (ex) {
      toast.error('Failed to fetch Completed Signature Agents report.')
      this.setState({ tableData: this._buildTableData([]) })
    }

    this.setState({ loading: false })
  }

  _buildTableData = rows => {
    return {
      columns: EMPTY_DATATABLE.columns,
      rows: rows.map((row, idx) => ({
        rank: idx + 1,
        agent: (
          <div className='profile-container'>
            <div className='img-wrapper'>
              <div className='profile-img-wrapper'>
                <img
                  src={getHeadshot({
                    id: row.user.get('id'),
                    u_picture: row.user.get('u_picture'),
                  })}
                  alt={`${row.user.get('u_fname')} ${row.user.get('u_lname')}`}
                />
              </div>
            </div>
            <div className='agent-name'>
              {row.user.get('u_fname')} {row.user.get('u_lname')}
            </div>
          </div>
        ),
        leader: row.leader,
        division: row.division,
        date: row.accomplishment.get('accomplished_at') ? (
          <>
            {moment.utc(row.accomplishment.get('accomplished_at')).tz(REPORT_TZ).format('MMM DD, YYYY')}
            <br />
            <RecognizeAgentBtn User={row.user} Accomplishment={row.accomplishment} />
          </>
        ) : (
          ''
        ),
      })),
    }
  }

  getPreviousData = () => {
    const { dateFrom } = this.state
    const date = subWeeks(dateFrom, 1)
    this.setState(
      {
        dateFrom: startOfWeek(date, { weekStartsOn: 1 }),
        dateTo: endOfWeek(date, { weekStartsOn: 1 }),
      },
      () => this._fetchReport()
    )
  }

  getCurrentData = () => {
    this.setState(
      {
        dateFrom: startOfWeek(this.state.currentDate, { weekStartsOn: 1 }),
        dateTo: endOfWeek(this.state.currentDate, { weekStartsOn: 1 }),
      },
      () => this._fetchReport()
    )
  }

  getNextData = () => {
    const { dateTo } = this.state
    const date = addWeeks(dateTo, 1)
    this.setState(
      {
        dateFrom: startOfWeek(date, { weekStartsOn: 1 }),
        dateTo: endOfWeek(date, { weekStartsOn: 1 }),
      },
      () => this._fetchReport()
    )
  }

  renderCurrentDatesHeader = () => {
    const { dateFrom, dateTo } = this.state
    const prevFrom = subWeeks(dateFrom, 1),
      prevTo = subWeeks(dateTo, 1)

    return (
      <MDBRow>
        <MDBCol size='12' md='6'>
          <div className='factor-explain'>
            <h3 className='text-center'>Completed Recorded Week #{getWeek(prevFrom)}</h3>
            <p className='text-center'>
              The agents below achieved Signature Agent Completed status during the previous business week.
            </p>
            <h4 className='text-center'>
              {format(prevFrom, 'MMM dd, yyyy')} - {format(prevTo, 'MMM dd, yyyy')}
            </h4>
          </div>
        </MDBCol>
        <MDBCol size='12' md='6'>
          <div className='factor-explain'>
            <h3 className='text-center'>Completed Awarding Week #{getWeek(dateFrom)}</h3>
            <p className='text-center'>
              The agents below will be recognized on this week's National Sales Meeting (NSM) for{' '}
              {format(dateFrom, 'EEEE, MMMM do')}.
            </p>
            <h4 className='text-center'>
              {format(dateFrom, 'MMM dd, yyyy')} - {format(dateTo, 'MMM dd, yyyy')}
            </h4>
          </div>
        </MDBCol>
      </MDBRow>
    )
  }

  render() {
    toast.configure()

    return (
      <Fragment>
        <DashboardLayout>
          <main className='mainSection mt-5 pb-5'>
            <MDBContainer fluid id='CompletedSigAgentsReportPage' className='WeeklyAdminReportPage'>
              <h3 className='text-center'>Admin Report: Completed Signature Agents</h3>
              <hr />
              {this.state.loading ? (
                <h2>Loading...</h2>
              ) : (
                <>
                  {this.renderCurrentDatesHeader()}
                  <MDBRow className='mt-2 dates-selector'>
                    <MDBCol className='text-center'>
                      <h5>Previous</h5>
                      <MDBBtn tag='a' flat className='text-primary' onClick={this.getPreviousData}>
                        Week
                      </MDBBtn>
                    </MDBCol>
                    <MDBCol className='text-center'>
                      <h5>Current</h5>
                      <MDBBtn tag='a' flat className='text-primary' onClick={this.getCurrentData}>
                        Week
                      </MDBBtn>
                    </MDBCol>
                    <MDBCol className='text-center'>
                      <h5>Next</h5>
                      <MDBBtn tag='a' flat className='text-primary' onClick={this.getNextData}>
                        Week
                      </MDBBtn>
                    </MDBCol>
                  </MDBRow>
                  <MDBRow className='mt-3'>
                    <MDBCol size='12'>
                      <p className='text-center text-muted small mb-0'>
                        Week ranges and dates shown in U.S. Eastern Time.
                      </p>
                    </MDBCol>
                  </MDBRow>

                  <MDBRow className='mt-4'>
                    <MDBCol size='12' className='content-wrapper'>
                      <MDB4Datatable
                        searching={false}
                        sortable={false}
                        paging={false}
                        displayEntries={false}
                        info={false}
                        noBottomColumns
                        responsive
                        striped
                        bordered
                        data={this.state.tableData || EMPTY_DATATABLE}
                        className='text-capitalize'
                      />
                    </MDBCol>
                  </MDBRow>
                </>
              )}
            </MDBContainer>
          </main>
        </DashboardLayout>
      </Fragment>
    )
  }
}

export default CompletedSigAgents
